import React, { useState, useEffect } from 'react';
import { GetConfig } from './../../../utilities/BL/Dinosaur';
import { ClearSocketMessageHandler, ConnectWebSocketForOverlay, DisconnectWebSocket, SetSocketMessageHandler } from './../../../utilities/BL/Zones';

export const DinosaurFan = ({ screenId: propScreenId }) => {
    const queryParams = new URLSearchParams(window.location.search);
    const screenId = parseInt(queryParams.get('screenId') || propScreenId || 1, 10);

    const [isLoading, setIsLoading] = useState(false);
    const [theme, setTheme] = useState(null);
    const [toast, setToast] = useState('');

    // Estados de la Partida controlada por el Admin
    const [gameStatus, setGameStatus] = useState('waiting'); // waiting, playing, gameover
    const [connectionStatus, setConnectionStatus] = useState('idle');

    useEffect(() => {
        loadConfigTheme();
        connectWS();

        return () => {
            ClearSocketMessageHandler();
            DisconnectWebSocket();
        };
    }, [screenId]);

    const loadConfigTheme = async () => {
        try {
            const configRes = await GetConfig(screenId);
            if (configRes.success && configRes.data?.themeId) {
                const response = await fetch(`${process.env.REACT_APP_API_URL}Dinosaur/GetTheme/${configRes.data.themeId}`);
                const data = await response.json();
                if (data.success) {
                    setTheme(data.data);
                }
            }
        } catch (e) {
            console.error("Fallo al obtener tema en celular:", e);
        }
    };

    const connectWS = async () => {
        const overlayType = `dinosaur:fan:${screenId}`;
        await ConnectWebSocketForOverlay(
            overlayType,
            (status) => setConnectionStatus(status),
            (toastMsg) => console.log(`[Dinosaur Fan WS] ${toastMsg}`)
        );
        SetSocketMessageHandler(handleIncomingMessage);
    };

    const handleIncomingMessage = (message) => {
        if (!message || !message.type) return;

        const type = message.type.toLowerCase();
        
        switch (type) {
            case 'dinosaur.cover':
            case 'dinosaur.prepare':
            case 'dinosaur.reset':
                setGameStatus('waiting');
                loadConfigTheme();
                break;
            case 'dinosaur.start':
                setGameStatus('playing');
                break;
            case 'dinosaur.gameover':
                setGameStatus('gameover');
                break;
            default:
                break;
        }
    };

    const getMediaUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http://') || path.startsWith('https://')) return path;
        const apiBase = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        return `${apiBase}${path}`;
    };

    const showToast = (msg) => {
        setToast(msg);
        setTimeout(() => setToast(''), 3000);
    };

    const handleStartPress = async () => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}Dinosaur/StartGame/${screenId}`, {
                method: 'POST'
            });
            if (response.ok) {
                setGameStatus('playing');
            } else {
                showToast('Error al iniciar partida');
            }
        } catch (error) {
            showToast('Error de conexión');
        } finally {
            setIsLoading(false);
        }
    };

    const handleJumpPress = async () => {
        if (isLoading || gameStatus !== 'playing') return;
        setIsLoading(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}Dinosaur/Jump/${screenId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    playerName: 'Aficionado'
                })
            });

            if (!response.ok) {
                showToast('Error al saltar');
            }
        } catch (error) {
            showToast('Error de conexión');
        } finally {
            setTimeout(() => {
                setIsLoading(false);
            }, 100);
        }
    };

    return (
        <>
            {/* Fondo dinámico a pantalla completa */}
            <div className='SelfieFan__Screen__QR__Background'>
                {theme?.fanBackgroundType === 'image' && theme?.fanBackgroundUrl ? (
                    <img 
                        src={getMediaUrl(theme.fanBackgroundUrl)} 
                        className='SelfieFan__Screen__QR__Background__Video' 
                        alt="Background" 
                    />
                ) : (
                    <div 
                        className='SelfieFan__Screen__QR__Background__Video' 
                        style={{ backgroundColor: theme?.fanBackgroundColor || '#000000' }}
                    />
                )}
            </div>

            <div className="slot-fan-container" style={{ position: 'relative', zIndex: 10, minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <header className="slot-fan-header">
                    <div className="px-3 pt-3 text-center">
                        {theme?.brandLogoUrl ? (
                            <img src={getMediaUrl(theme.brandLogoUrl)} alt="Brand Logo" className='slot-fan-logo' style={{ maxHeight: '75px', objectFit: 'contain' }} />
                        ) : (
                            <img src="/img/page/adsl-five.png" alt="Logo Adsl Five" className='slot-fan-logo'/>
                        )}
                        <p className="d-block small mb-0 text-center text-white-50">Jugando en pantalla #{screenId}</p>
                    </div>
                </header>

                <div className='slot-fan-lever-area flex-grow-1 d-flex flex-column align-items-center justify-content-center px-4'>
                    {gameStatus === 'waiting' && (
                        <div className="d-flex flex-column align-items-center">
                            <p className="text-white small mb-4 fw-semibold text-shadow" style={{ textShadow: '1px 1px 2px #000' }}>La máquina está lista. Presiona el botón para iniciar.</p>
                            <button
                                onClick={handleStartPress}
                                disabled={isLoading}
                                className="btn rounded-circle d-flex align-items-center justify-content-center border-0 shadow-lg position-relative transition-all active:scale-95 animate__animated animate__pulse animate__infinite"
                                style={{
                                    width: '180px',
                                    height: '180px',
                                    color: theme?.fanButtonType === 'image' ? 'transparent' : '#000000',
                                    fontSize: '20px',
                                    fontWeight: 'bold',
                                    boxShadow: theme?.fanButtonType === 'image' ? '0 10px 25px rgba(0,0,0,0.4)' : `0 10px 25px rgba(0,0,0,0.5), inset 0 -6px 0px rgba(0,0,0,0.3)`,
                                    fontFamily: "'Press Start 2P', cursive",
                                    lineHeight: '1.3',
                                    background: theme?.fanButtonType === 'image' && theme.fanButtonUrl 
                                        ? `url(${getMediaUrl(theme.fanButtonUrl)}) center/cover no-repeat` 
                                        : (theme?.fanButtonColor || '#ffc107')
                                }}
                            >
                                {theme?.fanButtonType === 'image' ? '' : 'INICIAR PARTIDA'}
                            </button>
                        </div>
                    )}

                    {gameStatus === 'playing' && (
                        <div className="d-flex flex-column align-items-center animate__animated animate__fadeIn">
                            <span className="badge px-4 py-2 fs-6 rounded-pill mb-4 shadow" style={{ backgroundColor: theme?.fanButtonType === 'image' ? '#ffc107' : (theme?.fanButtonColor || '#ffc107'), color: '#000000', fontWeight: 'bold' }}>
                                Jugador: Aficionado
                            </span>
                            <p className="text-white small mb-4 fw-semibold text-shadow" style={{ textShadow: '1px 1px 2px #000' }}>¡Presiona el botón para saltar obstáculos!</p>
                            <button
                                onClick={handleJumpPress}
                                disabled={isLoading}
                                className="btn rounded-circle d-flex align-items-center justify-content-center border-0 shadow-lg position-relative transition-all active:scale-95"
                                style={{
                                    width: '180px',
                                    height: '180px',
                                    color: theme?.fanButtonType === 'image' ? 'transparent' : '#000000',
                                    fontSize: '22px',
                                    fontWeight: 'bold',
                                    boxShadow: theme?.fanButtonType === 'image' ? '0 10px 25px rgba(0,0,0,0.4)' : `0 10px 25px rgba(0,0,0,0.5), inset 0 -6px 0px rgba(0,0,0,0.3)`,
                                    fontFamily: "'Press Start 2P', cursive",
                                    background: theme?.fanButtonType === 'image' && theme.fanButtonUrl 
                                        ? `url(${getMediaUrl(theme.fanButtonUrl)}) center/cover no-repeat` 
                                        : (theme?.fanButtonColor || '#ffc107')
                                }}
                            >
                                {theme?.fanButtonType === 'image' ? '' : 'SALTAR'}
                            </button>
                        </div>
                    )}

                    {gameStatus === 'gameover' && (
                        <div className="casino-card text-center p-4 rounded-4 shadow" style={{ backgroundColor: 'rgba(0,0,0,0.75)', border: '2px solid #dc3545', maxWidth: '350px' }}>
                            <h4 className="text-danger fw-bold mb-2">PARTIDA TERMINADA</h4>
                            <p className="text-white-50 small mb-0">Esperando que el administrador reinicie la partida...</p>
                            <div className="spinner-border text-warning mt-3" role="status">
                                <span className="visually-hidden">Cargando...</span>
                            </div>
                        </div>
                    )}
                </div>

                <p className='text-center FooterCredits text-white-50 py-3 mb-0' style={{ fontSize: '11px', zIndex: 5 }}>Hecho con ♥ por Interaktia | 2026</p>
            </div>

            {toast && (
                <div className="toast-container position-fixed bottom-0 start-50 translate-middle-x mb-3" style={{ zIndex: 1050 }}>
                    <div className="toast show bg-dark text-white px-3 py-2 shadow rounded-3 border-0">
                        {toast}
                    </div>
                </div>
            )}
        </>
    );
};
